/**
 * Quiz & LMS - interactive renderer prototype
 *
 * Access: http://flownotes.localhost/?test=quiz
 *
 * Uses production Preview / Quiz / Results / Runs UI with local mock scoring
 * (no migrations or /api/quiz required). Bet: Quiz & LMS Extension 2026-07-15.
 */

import { useCallback, useMemo, useState } from 'react'
import { Eye, GraduationCap, MoreHorizontal } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { QuizActiveMode } from '@/components/integrations/quiz/QuizActiveMode'
import { QuizPreviewMode } from '@/components/integrations/quiz/QuizPreviewMode'
import { QuizResultsMode } from '@/components/integrations/quiz/QuizResultsMode'
import { QuizRunsTab } from '@/components/integrations/quiz/QuizRunsTab'
import type { QuizRun } from '@/lib/api/quiz'
import { parseQuizFromContent, type QuizQuestion } from '@/lib/quiz-parser'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'

const PASSING_SCORE = 70
/** Short limit so the timer is obvious in the prototype. */
const DEMO_TIME_LIMIT_MINUTES = 5

const MOCK_QUIZ_MARKDOWN = `---
kind: quiz
slug: owner-builder-exam-mock-demo
title: Owner Builder Exam - Prototype Mock
quiz_config:
  time_limit_minutes: 5
  randomize: false
  passing_score: 70
---

> Prototype mock (8 questions). Submit scores locally - no API.

\`\`\`yaml quiz
questions:
  - id: q1
    domain: bc_building_code
    stem: "Under BCBC, the minimum ceiling height for a habitable room is..."
    options:
      a: "2.1 m"
      b: "2.3 m"
      c: "2.4 m"
      d: "2.6 m"
    answer: b
    explanation: "Section 9.5.1 - habitable rooms require 2.3 m minimum."
    difficulty: medium
  - id: q2
    domain: house_framing
    stem: "Maximum allowable notch depth in a load-bearing stud is..."
    options:
      a: "1/6 of stud depth"
      b: "1/4 of stud depth"
      c: "1/3 of stud depth"
      d: "40 mm absolute"
    answer: a
    explanation: "BCBC 9.23.3 - notches in bearing studs limited to 1/6 depth."
    difficulty: hard
  - id: q3
    domain: bc_energy_step_code
    stem: "Step Code Level 3 roughly targets what airtightness (ACH50) for Part 9 homes?"
    options:
      a: "1.5"
      b: "2.5"
      c: "3.5"
      d: "5.0"
    answer: b
    explanation: "Step 3 commonly targets ~2.5 ACH50 (jurisdiction tables vary)."
    difficulty: medium
  - id: q4
    domain: hpa_regulatory
    stem: "An owner-builder authorization is typically required when the owner..."
    options:
      a: "hires a licensed builder for the entire project"
      b: "constructs or renovates a home they intend to occupy"
      c: "only paints interior walls"
      d: "orders lumber for a shed under 10 m2"
    answer: b
    explanation: "HPA owner-builder path applies when the owner builds for own occupancy."
    difficulty: easy
  - id: q5
    domain: site_safety
    stem: "Fall protection is generally required when working at heights of..."
    options:
      a: "1.2 m or more"
      b: "1.8 m or more"
      c: "3.0 m or more"
      d: "only on commercial sites"
    answer: c
    explanation: "WorkSafeBC fall protection threshold is commonly 3 m (check current regs)."
    difficulty: easy
  - id: q6
    domain: electrical_plumbing
    stem: "A GFCI receptacle is required in which residential location?"
    options:
      a: "Bedroom closet"
      b: "Living room floor outlet"
      c: "Bathroom within 1.5 m of a sink"
      d: "Attic junction box"
    answer: c
    explanation: "CEC requires GFCI protection for bathroom receptacles near sinks."
    difficulty: medium
  - id: q7
    domain: bc_building_code
    stem: "Egress windows in bedrooms must provide a clear opening of at least..."
    options:
      a: "0.25 m2 with 380 mm min dimension"
      b: "0.35 m2 with 380 mm min dimension"
      c: "0.50 m2 with 450 mm min dimension"
      d: "No minimum if smoke alarms are present"
    answer: b
    explanation: "BCBC bedroom egress: 0.35 m2 clear opening, 380 mm min dimension."
    difficulty: hard
  - id: q8
    domain: house_framing
    stem: "Joist hangers are typically required when..."
    options:
      a: "joists bear fully on a plate with adequate lap"
      b: "joists frame into a beam or header without adequate bearing"
      c: "using only solid lumber rafters"
      d: "the span is under 2 m"
    answer: b
    explanation: "Hangers transfer load when joists do not have full bearing on support."
    difficulty: medium
\`\`\`
`

const SEED_RUNS: QuizRun[] = [
  {
    id: 'run-seed-1',
    score_pct: 62.5,
    domain_scores: {
      bc_building_code: 50,
      house_framing: 50,
      bc_energy_step_code: 100,
      hpa_regulatory: 100,
      site_safety: 0,
      electrical_plumbing: 100,
    },
    weak_domains: ['bc_building_code', 'house_framing', 'site_safety'],
    passed: false,
    time_taken_s: 420,
    completed_at: '2026-07-10T18:00:00.000Z',
  },
  {
    id: 'run-seed-2',
    score_pct: 75,
    domain_scores: {
      bc_building_code: 100,
      house_framing: 50,
      bc_energy_step_code: 100,
      hpa_regulatory: 100,
      site_safety: 100,
      electrical_plumbing: 0,
    },
    weak_domains: ['house_framing', 'electrical_plumbing'],
    passed: true,
    time_taken_s: 380,
    completed_at: '2026-07-12T19:30:00.000Z',
  },
]

type QuizTab = 'preview' | 'quiz' | 'results' | 'runs'

interface LastResult {
  score_pct: number
  domain_scores: Record<string, number>
  weak_domains: string[]
  passed: boolean
  time_taken_s: number
}

function scoreAnswers(questions: QuizQuestion[], answers: Record<string, string>) {
  const domainTotals: Record<string, number> = {}
  const domainCorrect: Record<string, number> = {}
  let correct = 0

  for (const q of questions) {
    const domain = q.domain || 'general'
    domainTotals[domain] = (domainTotals[domain] || 0) + 1
    const userAnswer = answers[q.id] ? String(answers[q.id]).toLowerCase() : null
    if (userAnswer === q.answer) {
      correct += 1
      domainCorrect[domain] = (domainCorrect[domain] || 0) + 1
    }
  }

  const total = questions.length || 1
  const scorePct = Math.round((correct / total) * 10000) / 100
  const domainScores: Record<string, number> = {}
  const weakDomains: string[] = []

  for (const domain of Object.keys(domainTotals)) {
    const dTotal = domainTotals[domain]
    const dCorrect = domainCorrect[domain] || 0
    const pct = Math.round((dCorrect / dTotal) * 10000) / 100
    domainScores[domain] = pct
    if (pct < PASSING_SCORE) weakDomains.push(domain)
  }

  return {
    score_pct: scorePct,
    domain_scores: domainScores,
    weak_domains: weakDomains,
    passed: scorePct >= PASSING_SCORE,
  }
}

export function QuizLmsPrototype() {
  const questions = useMemo(() => parseQuizFromContent(MOCK_QUIZ_MARKDOWN), [])
  const [tab, setTab] = useState<QuizTab>('quiz')
  const [submitting, setSubmitting] = useState(false)
  const [lastResult, setLastResult] = useState<LastResult | null>(null)
  const [runs, setRuns] = useState<QuizRun[]>(SEED_RUNS)
  const [quizKey, setQuizKey] = useState(0)

  const bestScore = useMemo(
    () => (runs.length ? Math.max(...runs.map((r) => r.score_pct)) : 0),
    [runs],
  )
  const trend = useMemo(() => {
    if (runs.length < 2) return 0
    const sorted = [...runs].sort((a, b) =>
      String(a.completed_at || '').localeCompare(String(b.completed_at || '')),
    )
    const first = sorted[0].score_pct
    const last = sorted[sorted.length - 1].score_pct
    return Math.round((last - first) * 100) / 100
  }, [runs])

  const handleSubmit = useCallback(
    async (answers: Record<string, string>, startedAt: Date) => {
      setSubmitting(true)
      try {
        await new Promise((r) => setTimeout(r, 350))
        const scored = scoreAnswers(questions, answers)
        const timeTakenS = Math.max(
          1,
          Math.round((Date.now() - startedAt.getTime()) / 1000),
        )
        const result: LastResult = { ...scored, time_taken_s: timeTakenS }
        setLastResult(result)
        setRuns((prev) => [
          {
            id: `run-local-${Date.now()}`,
            ...result,
            completed_at: new Date().toISOString(),
          },
          ...prev,
        ])
        setTab('results')
        toast.success(`Prototype scored: ${result.score_pct}% (local only)`)
      } finally {
        setSubmitting(false)
      }
    },
    [questions],
  )

  const handleRetake = useCallback(() => {
    setLastResult(null)
    setQuizKey((k) => k + 1)
    setTab('quiz')
  }, [])

  return (
    <div
      className="h-screen flex flex-col bg-background text-foreground"
      data-testid={testIds.quizDocumentRenderer.root}
    >
      <div className="px-4 py-2 border-b border-border bg-teal-500/10 text-sm text-muted-foreground shrink-0">
        <strong className="text-foreground">Quiz & LMS prototype</strong>
        {' - '}
        <code className="text-xs">/?test=quiz</code>
        {' - '}
        production tab UI + local scoring (no API / migrations). Demo timer:{' '}
        {DEMO_TIME_LIMIT_MINUTES} min.
      </div>

      <div className="px-4 py-3.5 border-b border-border flex items-start gap-3 shrink-0">
        <div className="w-9 h-9 rounded-md bg-teal-500/15 text-teal-600 flex items-center justify-center shrink-0">
          <GraduationCap className="h-[18px] w-[18px]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold truncate">
              Owner Builder Exam - Prototype Mock
            </h2>
            <Badge variant="outline" className="text-xs shrink-0">
              {questions.length} questions
            </Badge>
            <Badge variant="secondary" className="text-xs shrink-0">
              prototype
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Slug: owner-builder-exam-mock-demo - {DEMO_TIME_LIMIT_MINUTES} min - pass{' '}
            {PASSING_SCORE}%
          </p>
        </div>
        {lastResult ? (
          <button
            type="button"
            className="text-xs text-teal-600 hover:underline shrink-0"
            onClick={handleRetake}
          >
            Retake quiz
          </button>
        ) : null}
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as QuizTab)}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="mx-4 mt-3 flex items-center gap-2 shrink-0">
          <TabsList className="w-auto justify-start">
            <TabsTrigger value="quiz" data-testid={testIds.quizDocumentRenderer.quizTab}>
              Quiz
            </TabsTrigger>
            <TabsTrigger value="results">Results</TabsTrigger>
            <TabsTrigger value="runs">Runs</TabsTrigger>
          </TabsList>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground"
                aria-label="Quiz setup and review"
                data-testid={testIds.quizDocumentRenderer.moreMenu}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                className="gap-2 text-xs"
                data-testid={testIds.quizDocumentRenderer.reviewQuestionsMenuItem}
                onClick={() => setTab('preview')}
              >
                <Eye className="h-3.5 w-3.5 flex-none" />
                Review questions (show answers)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <TabsContent
          value="preview"
          className="mt-0 flex-1 min-h-0 overflow-hidden flex flex-col"
        >
          <div className="mx-4 mt-3 mb-1 flex items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs shrink-0">
            <span>Review mode - answers visible (setup / admin).</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs shrink-0"
              onClick={() => setTab('quiz')}
            >
              Back to quiz
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <QuizPreviewMode questions={questions} />
          </div>
        </TabsContent>

        <TabsContent
          value="quiz"
          forceMount
          className={
            tab === 'quiz'
              ? 'mt-0 flex-1 min-h-0 overflow-hidden flex flex-col'
              : 'mt-0 flex-1 min-h-0 overflow-hidden flex-col hidden'
          }
        >
          <QuizActiveMode
            key={quizKey}
            questions={questions}
            timeLimitMinutes={DEMO_TIME_LIMIT_MINUTES}
            slug="demo-quiz-lms"
            documentId="demo-quiz-lms"
            onSubmit={handleSubmit}
            submitting={submitting}
            onStartOver={() => setQuizKey((k) => k + 1)}
          />
        </TabsContent>

        <TabsContent value="results" className="mt-0 flex-1 min-h-0 overflow-hidden">
          {lastResult ? (
            <QuizResultsMode
              scorePct={lastResult.score_pct}
              passingScore={PASSING_SCORE}
              passed={lastResult.passed}
              domainScores={lastResult.domain_scores}
              weakDomains={lastResult.weak_domains}
              timeTakenS={lastResult.time_taken_s}
              onGenerateDrill={
                lastResult.weak_domains.length > 0
                  ? () =>
                      toast.message('Drill CTA', {
                        description:
                          'In production this opens chat to generate a weak-domain drill.',
                      })
                  : undefined
              }
            />
          ) : (
            <p className="text-sm text-muted-foreground p-6">
              Complete a quiz run (Quiz tab) to see results here.
            </p>
          )}
        </TabsContent>

        <TabsContent value="runs" className="mt-0 flex-1 min-h-0 overflow-hidden">
          <QuizRunsTab
            runs={runs}
            loading={false}
            bestScore={bestScore}
            trend={trend}
            passingScore={PASSING_SCORE}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
